import { FC, ReactNode, useMemo, useRef, useState } from 'react';

import { usePlatformConfig } from '@belong/device-bridge';
import { PLATFORM_TYPE } from '@belong/types';
import { AccentStrip, ACCENTSTRIP_VARIANTS } from '@belong/ui-core';

import { useDisallowScrollToTop } from '../lib/useDisallowScrollToTop';
import { useUpdateChatIframe } from '../lib/useUpdateChatIframe';
import { useNotificationForConfirmation } from '../lib/useNotificationForConfirmation';
import { LIVE_CHAT_TEST_IDS } from '../lib/testIds';
import { ChatContext } from '../lib/ChatContext';

import { ChatInput } from './ChatInput/ChatInput';
import { ChatButton } from './ChatButton/ChatButton';
import { ChatTranscript } from './Transcript/ChatTranscript';
import { LoadingIndicator } from './LoadingIndicator/LoadingIndicator';
import { AgentTyping } from './AgentTyping';
import { ChatFeedback } from './Feedback/ChatFeedback';
import { ChatQueuePosition } from './ChatQueuePosition';
import { ChatMessage } from './ChatMessage/ChatMessage';
import { ChatQuitModal } from './ChatQuitModal';
import { ChatWindow } from './ChatWindow/ChatWindow';
import { ChatTitle, ChatTitleBar, CloseButton, MinimizeButton } from './elements';
import { SkipLinkPortal } from './SkipLinkPortal';
import { SkipToChatLink } from './SkipToChatLink';

import CONTENT from './content.json';

export const focusOnTitle = () => {
  document.getElementById(LIVE_CHAT_TEST_IDS.TITLE)?.focus();
};

export const focusOnOpenButton = () => {
  document.getElementById(LIVE_CHAT_TEST_IDS.OPEN_BUTTON)?.focus();
};

export interface IChatProps {
  isOpen?: boolean;
  isFullScreen?: boolean;
  showLoading?: boolean;
  isAgentTyping?: boolean;
  showFeedback?: boolean;
  showTextInput?: boolean;
  showQueuePosition?: boolean;
  errorMessage?: string;
  transcript?: connect.ChatTranscriptItem[];
  canAddAttachment?: boolean;
  textInputEnabled?: boolean;
  titleBarContent?: ReactNode;
  contactId?: string;
  unreadCount?: number;
  showQuitConfirmation?: boolean;
  isScanningAttachment: boolean;
  downloadAttachment: (attachmentId: string) => Promise<Blob | undefined>;
  onQuitConfirm?: () => void;
  onQuitCancel?: () => void;
  onClickOpen?: () => void;
  onClickMinimize?: () => void;
  onClickClose?: () => void;
  onPickerItemSelect?: (item: connect.ListPickerItem) => void;
  onTypeMessage?: (text: string) => void;
  onSendMessage?: (text: string) => void;
  onSendAttachment: (
    attachment: File
  ) => Promise<connect.ParticipantServiceResponse<connect.SendAttachmentResult> | undefined>;
}

// Display errors as though they are incoming messages.
const makeAbsoluteTime = () => new Date().toISOString();
const ChatErrorMessage: FC<{ message: string }> = ({ message }) => (
  <ChatMessage
    item={{
      AbsoluteTime: makeAbsoluteTime(),
      Id: 'belong_client_error',
      Type: 'MESSAGE',
      ParticipantRole: 'SYSTEM',
      Content: message
    }}
  />
);
ChatErrorMessage.displayName = 'ChatErrorMessage';

const useSkipHref = (isOpen?: boolean): string => {
  // If chat is iframed and the skip button is rendered in the parent window, it cannot link to individual
  // elements within the iframe, so links to the iframe itself.
  return useMemo(() => {
    const iframeId = typeof window !== 'undefined' && window.frameElement?.id;
    const specificTargetId = isOpen ? LIVE_CHAT_TEST_IDS.TITLE : LIVE_CHAT_TEST_IDS.OPEN_BUTTON;

    return `#${iframeId || specificTargetId}`;
  }, [isOpen]);
};

export const Chat: FC<IChatProps> = ({
  isOpen,
  isFullScreen,
  showLoading,
  isAgentTyping,
  isScanningAttachment,
  showFeedback,
  showTextInput,
  showQueuePosition,
  errorMessage,
  transcript,
  canAddAttachment,
  textInputEnabled = true,
  titleBarContent = 'How can we help?',
  contactId,
  unreadCount,
  showQuitConfirmation,
  downloadAttachment,
  onQuitConfirm,
  onQuitCancel,
  onClickOpen,
  onClickMinimize,
  onClickClose,
  onPickerItemSelect,
  onTypeMessage,
  onSendMessage,
  onSendAttachment
}) => {
  const [addAttachmentVisible, setAddAttachmentVisible] = useState(false);
  const skipTargetRef = useRef(null);
  const skipFallbackHref = useSkipHref(isOpen);
  const isAndroid = usePlatformConfig().config.platform === PLATFORM_TYPE.ANDROID;
  useDisallowScrollToTop(isAndroid && isOpen);
  useUpdateChatIframe({ isOpen, isFullScreen, modalVisible: showQuitConfirmation || addAttachmentVisible });

  useNotificationForConfirmation(transcript);

  return (
    <>
      <SkipLinkPortal>
        <SkipToChatLink targetRef={skipTargetRef} href={skipFallbackHref} />
      </SkipLinkPortal>
      {showQuitConfirmation && <ChatQuitModal onConfirm={onQuitConfirm} onCancel={onQuitCancel} />}
      <ChatButton
        ref={!isOpen ? skipTargetRef : undefined}
        visible={!isOpen}
        onClick={onClickOpen}
        unreadCount={unreadCount}
      />
      {isOpen && (
        <ChatContext.Provider value={{ onPickerItemSelect }}>
          <ChatWindow
            data-testid={LIVE_CHAT_TEST_IDS.WINDOW}
            aria-labelledby={LIVE_CHAT_TEST_IDS.TITLE}
            isFullScreen={isFullScreen}
          >
            <ChatTitleBar>
              <ChatTitle
                ref={skipTargetRef}
                id={LIVE_CHAT_TEST_IDS.TITLE}
                data-testid={LIVE_CHAT_TEST_IDS.TITLE}
                tabIndex={-1}
              >
                {titleBarContent}
              </ChatTitle>
              <MinimizeButton
                onClick={onClickMinimize}
                data-testid={LIVE_CHAT_TEST_IDS.MINIMIZE_BUTTON}
                aria-label={CONTENT.ariaLabels.minimize}
              />
              <CloseButton
                onClick={onClickClose}
                data-testid={LIVE_CHAT_TEST_IDS.CLOSE_BUTTON}
                aria-label={CONTENT.ariaLabels.close}
              />
            </ChatTitleBar>
            <AccentStrip variant={ACCENTSTRIP_VARIANTS.REVERSE_FOIL_SLICK} isSmall isBottom />
            <ChatTranscript transcript={transcript} downloadAttachment={downloadAttachment}>
              {errorMessage && <ChatErrorMessage message={errorMessage} />}
              {showLoading && <LoadingIndicator />}
              {isAgentTyping && <AgentTyping />}
              {showQueuePosition && <ChatQueuePosition contactId={contactId} />}
            </ChatTranscript>
            {showFeedback && <ChatFeedback onClickClose={onClickClose} contactId={contactId} />}
            {showTextInput && (
              <ChatInput
                canAddAttachment={canAddAttachment}
                disabled={!textInputEnabled}
                isScanningAttachment={isScanningAttachment}
                onTypeMessage={onTypeMessage}
                onSendMessage={onSendMessage}
                onSendAttachment={onSendAttachment}
                setAddAttachmentVisible={setAddAttachmentVisible}
              />
            )}
          </ChatWindow>
        </ChatContext.Provider>
      )}
    </>
  );
};

Chat.displayName = 'Chat';
